/**
 * src/lib/voice/turnLatency.js — Per-turn user-perceived latency tracker
 *
 * Captures four marks per voice turn so the Service B rebuild can prove
 * its win against a recorded baseline:
 *
 *   speech_end    user stopped speaking (mic stop, VAD final)
 *   first_partial first non-empty partial transcript
 *   first_token   first SSE token from agent
 *   first_audio   first TTS audio byte played (source.start in WebAudio)
 *
 * Derived metrics use speech_end as t=0 because that is when the user
 * starts waiting. Emits one app_error_logs row per turn via logVoiceEvent
 * at flush() time.
 *
 * 2026-06-06 (Conversation Telemetry Baseline, Phase 1) — the flushed
 * turn_latency row now also carries the end-to-end SEGMENT breakdown the CEO
 * actually waits through, keyed by the turn trace_id:
 *   total_ms  speech_end  -> first_audio   (the whole user-perceived wait)
 *   agent_ms  speech_end  -> first_token   (agent think + RAG + first token)
 *   tts_ms    first_token -> first_audio   (TTS synthesis + decode + play)
 * agent_ms + tts_ms == total_ms whenever first_token + first_audio both land
 * (the window opens at speech_end, so there is no overlap between them).
 *   stt_ms    a PRE-WINDOW sub-metric: the client STT-accept cost measured in
 *             handleTranscript BEFORE this tracker exists, supplied via
 *             flush({ stt_ms }). It is reported alongside but is NOT part of
 *             the speech_end->first_audio window (STT precedes speech_end's
 *             send), so it deliberately does not sum into total_ms.
 * NOTE on first_partial: in the live voice pipeline partial transcripts arrive
 * DURING recording, before speech_end mints this tracker, so markFirstPartial
 * never fires in production. agent_ms is therefore keyed off first_token
 * (always available) rather than first_partial — the mark is kept only as an
 * optional diagnostic (time_to_partial_ms) for any future inline-STT path.
 * CARL completeness: trace_id + user_id (the real auth uid) are threaded
 * through to logVoiceEvent so the row maps to app_error_logs.trace_id /
 * user_id and joins the same turn's stt/packet_built/agent/tts rows under ONE
 * trace_id.
 *
 * Usage:
 *   const t = createTurnLatencyTracker(turnId, { traceId, userId });
 *   ...
 *   t.markSpeechEnd();
 *   t.markFirstPartial();
 *   t.markFirstToken();
 *   t.markFirstAudio();
 *   t.flush();          // emits 'turn_latency' event
 *   t.flush();          // no-op (idempotent)
 */

import { logVoiceEvent } from './voiceLogger.js';

/**
 * @param {string} turnId - Stable identifier for this turn (e.g. session-id + sequence)
 * @param {{ traceId?: string|null, userId?: string|null, sttModel?: string|null }} [opts] - CARL
 *   correlation. traceId MUST be the same trace_id threaded through the turn's
 *   stt / packet_built / agent / tts emits so /admin/observability joins them.
 *   sttModel — the STT model used for this turn (VFL-0.1). Carried through to
 *   the turn_latency emit so VFL-0.2/0.3 A/B work can slice timing by model.
 *   Defaults to 'gpt-realtime-whisper' (the current intentional default);
 *   VFL-0.2 will populate localStorage.acn_stt_model via a Settings toggle.
 * @returns {{
 *   markSpeechEnd:    () => void,
 *   markFirstPartial: () => void,
 *   markFirstToken:   () => void,
 *   markFirstAudio:   () => void,
 *   snapshot:         () => { speech_end: number|null, first_partial: number|null, first_token: number|null, first_audio: number|null },
 *   flush:            (extraMeta?: object) => void,
 * }}
 */
// VFL-0.1 — default STT model. VFL-0.2 will read from
// localStorage.acn_stt_model; until that toggle ships, every turn uses this.
// llm-registry-ok: src/ runs in browser — cannot use Node.js readFileSync-based getModel().
// This is the STT proxy model identifier (registry: realtime_voice role → gpt-realtime-whisper).
const DEFAULT_STT_MODEL = 'gpt-realtime-whisper'; // llm-registry-ok: src/ browser bundle; registry: realtime_voice role

/**
 * Resolve the STT model for latency tracking.
 * First looks at the explicit opts.sttModel (passed at creation time by
 * useVoiceConversation — the forward-compatible path that covers EVERY flush
 * path including the cleanup flush). Falls back to localStorage.acn_stt_model
 * (VFL-0.2 will populate this via the Settings toggle), then to the hardcoded
 * default. Guarded for non-browser / test environments.
 */
function resolveSttModel(optsModel) {
  if (optsModel) return optsModel;
  if (typeof localStorage !== 'undefined') {
    try {
      const stored = localStorage.getItem('acn_stt_model');
      if (stored) return stored;
    } catch {
      // spine-emit-ok: localStorage read can throw in sandboxed/privacy-mode
      // browsers; the default STT model is the benign fallback, not an error.
    }
  }
  return DEFAULT_STT_MODEL;
}

export function createTurnLatencyTracker(turnId, opts = {}) {
  const t0 = Date.now();
  const traceId = opts.traceId ?? null;
  const userId = opts.userId ?? null;
  // VFL-0.1 — capture stt_model at tracker creation so every flush path
  // (normal TTS-drained flush AND the cleanup-path flush) carries the same
  // value without requiring callers to pass it in extraMeta.
  const sttModel = resolveSttModel(opts.sttModel ?? null);
  const marks = {
    speech_end: null,
    first_partial: null,
    first_token: null,
    first_audio: null,
  };
  let flushed = false;

  const setOnce = (key) => {
    if (marks[key] === null) marks[key] = Date.now() - t0;
  };

  // Delta of a mark relative to speech_end (t=0 for the user-perceived wait).
  const delta = (markName) => {
    if (marks[markName] === null || marks.speech_end === null) return null;
    return marks[markName] - marks.speech_end;
  };

  // Segment between two marks; null if either boundary is missing.
  const segment = (fromMark, toMark) => {
    if (marks[fromMark] === null || marks[toMark] === null) return null;
    return marks[toMark] - marks[fromMark];
  };

  return {
    markSpeechEnd:    () => setOnce('speech_end'),
    markFirstPartial: () => setOnce('first_partial'),
    markFirstToken:   () => setOnce('first_token'),
    markFirstAudio:   () => setOnce('first_audio'),

    snapshot: () => ({ ...marks }),

    flush: (extraMeta = {}) => {
      if (flushed) return;
      flushed = true;
      try {
        logVoiceEvent('turn_latency', 'ok', `turn ${turnId} latency snapshot`, {
          turn_id: turnId,
          // CARL correlation — same trace_id as the turn's other emits.
          trace_id: traceId,
          user_id: userId,
          // VFL-0.1 — STT model used for this turn. Enables VFL-0.2/0.3 A/B
          // timing slices by model. Captured at creation so the cleanup-path
          // flush (which may fire before turnFlushMetaRef is set) always has it.
          stt_model: sttModel,
          // End-to-end user-perceived wait + per-stage segments. The window
          // opens at speech_end so agent_ms + tts_ms == total_ms (no overlap).
          // stt_ms is left null here on purpose — it is a pre-window client
          // measurement supplied via flush({ stt_ms }) (see extraMeta below).
          total_ms: delta('first_audio'),
          stt_ms:   null,
          agent_ms: delta('first_token'),
          tts_ms:   segment('first_token', 'first_audio'),
          // Legacy deltas (kept for any existing dashboards).
          time_to_partial_ms:     delta('first_partial'),
          time_to_first_token_ms: delta('first_token'),
          time_to_first_audio_ms: delta('first_audio'),
          marks: { ...marks },
          // extraMeta last so callers can override a mark-derived segment with
          // a precise server-measured value (e.g. agent_ms from api/ai.js).
          ...extraMeta,
        });
      } catch {
        /* logging must never break the voice path */
      }
    },
  };
}
